import { useEffect, useState } from "react";
import { getBranding } from "@v0y4ge/api-client";
import { onTenantChange, onTenantSwitching } from "../../shell/access";

/**
 * useBranding — shared, cached read of the workspace branding (primary/cover
 * colours + heading/body fonts). One fetch hydrates a module-level cache via
 * the typed `getBranding` choke point (MSW mock in this env / real infra on
 * deploy); every consumer re-renders when it lands. Surfaces that must reflect
 * the brand (landing pages, hubs, email) read this instead of hardcoding.
 */
export interface Branding {
  name?: string;
  primary?: string;
  cover?: string;
  link?: string;
  logoUrl?: string;
  headingFont?: string;
  bodyFont?: string;
}

let _cache: Branding | null = null;
let _loadStarted = false;
const _subs = new Set<() => void>();
const _emit = () => _subs.forEach((f) => { try { f(); } catch { /* noop */ } });
// Cross-tenant leak fix (docket 2026-07-26, same class as the store switcher):
// branding (logo/colours) is highly visible tenant identity — must purge + refetch on switch.
onTenantSwitching(() => { _cache = null; _loadStarted = false; _emit(); });
onTenantChange(() => { void _load(); });

async function _load(): Promise<void> {
  if (_loadStarted) return;
  _loadStarted = true;
  try {
    const r = await (getBranding as (a: unknown) => Promise<Record<string, unknown>>)({});
    const b = ((r as { branding?: Record<string, unknown>; data?: Record<string, unknown> }).branding
      ?? (r as { data?: Record<string, unknown> }).data ?? r) as Record<string, unknown>;
    _cache = {
      name: (b.name ?? b.workspaceName) as string | undefined,
      primary: (b.brandColor ?? b.primaryColor ?? b.primary) as string | undefined,
      cover: (b.coverColor ?? b.cover) as string | undefined,
      link: (b.link ?? b.linkColor) as string | undefined,
      logoUrl: (b.logoUrl ?? b.logo_url ?? b.logo) as string | undefined,
      headingFont: b.headingFont as string | undefined,
      bodyFont: b.bodyFont as string | undefined,
    };
  } catch {
    _cache = {};
  }
  _emit();
}

export function useBranding(): Branding {
  const [, force] = useState(0);
  useEffect(() => {
    const cb = () => force((n) => n + 1);
    _subs.add(cb);
    void _load();
    return () => { _subs.delete(cb); };
  }, []);
  return _cache ?? {};
}
